import { useEffect, useState } from "react";
import { Mail, Sparkles, Trash2, CheckCircle2, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/* Reservation Detected — bookings captured from forwarded emails (SendGrid inbound).
   Nothing is auto-saved: user reviews, renames, then adds to My Travel Hub or ignores. */
export default function PendingImports({ onImported }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/email-in/pending").then((r) => setItems(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!items.length) return null;

  const dismiss = async (pid) => {
    try { await api.delete(`/email-in/pending/${pid}`); setItems((l) => l.filter((x) => x.id !== pid)); }
    catch { toast.error("Could not dismiss"); }
  };
  const addAll = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.post("/trip-import/confirm", { trips: p.trips });
      await api.delete(`/email-in/pending/${p.id}`);
      setItems((l) => l.filter((x) => x.id !== p.id));
      toast.success(`${data.created.length} trip${data.created.length > 1 ? "s" : ""} added from your email`);
      onImported?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add the reservation"); }
    finally { setBusy(null); }
  };

  return (
    <div className="mb-6 space-y-3" data-testid="pending-imports">
      {items.map((p) => (
        <div key={p.id} className="border-2 border-violet-400 bg-violet-50/40" data-testid={`pending-import-${p.id}`}>
          <div className="bg-violet-600 text-white px-3 py-1.5 flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-widest">
            <Mail className="w-4 h-4" /> Reservation detected from your email
          </div>
          <div className="p-3">
            <div className="text-sm font-700 truncate">{p.subject}</div>
            <div className="text-xs text-muted-foreground">Forwarded from {p.from}</div>
            {p.status === "processing" ? (
              <div className="flex items-center gap-2 text-sm text-violet-700 mt-2">
                <RefreshCw className="w-4 h-4 animate-spin" /> AI is reading the booking…
              </div>
            ) : (
              <>
                <ul className="mt-2 space-y-1 text-xs text-zinc-700">
                  {(p.trips || []).map((t, i) => (
                    <li key={i} className="flex flex-wrap items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-violet-600" />
                      <b>{t.title}</b>
                      {t.start_date && <span>· {t.start_date}{t.end_date && t.end_date !== t.start_date ? ` → ${t.end_date}` : ""}</span>}
                      <span>· {(t.segments || []).length} leg{(t.segments || []).length !== 1 ? "s" : ""}</span>
                      {t.overlaps && (
                        <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5">
                          <AlertTriangle className="w-3 h-3" /> overlaps “{t.overlaps.title}”
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => addAll(p)} disabled={busy === p.id} data-testid={`pending-add-${p.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 bg-violet-700 text-white text-xs font-700 hover:bg-violet-600 disabled:opacity-50">
                    <CheckCircle2 className="w-4 h-4" /> Add to My Travel Hub
                  </button>
                  <button onClick={() => dismiss(p.id)} data-testid={`pending-ignore-${p.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 border border-zinc-300 text-zinc-600 text-xs font-700 hover:bg-zinc-50">
                    <Trash2 className="w-4 h-4" /> Ignore
                  </button>
                </div>
                <p className="text-[10px] text-muted-foreground mt-2">You can rename, edit legs or merge after adding — everything stays editable in the trip.</p>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
